import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { arrayMove } from "@dnd-kit/sortable";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { createTaskApi } from "../api/taskApi";
import { isDueTodayOrOverdue, sortTasksForStaff, isDueToday, isOverdue } from "../utils/taskDates";
import { detectNewTasks, notifyNewTask } from "../utils/taskNotifications";
import {
    syncTaskRemindersFromTasks,
    cancelTaskReminder,
    handleTaskReminderDeepLink,
    isRecurringTask,
    findMissedReminders,
    notifyMissedReminders,
} from "../utils/taskReminderSync";
import { useTaskEvents } from "./useTaskEvents";

const POLL_MS = 30_000;
const POLL_MS_REALTIME = 90_000;

export function useTasks({ role, apiUrl, getToken, enablePolling = true }) {
    const api = useMemo(() => createTaskApi({ role, apiUrl, getToken }), [role, apiUrl, getToken]);
    const isStaff = role === "staff";

    const [tasks, setTasks] = useState([]);
    const [statusFilter, setStatusFilter] = useState("");
    const [priorityFilter, setPriorityFilter] = useState("");
    const [viewFilter, setViewFilter] = useState(isStaff ? "today" : "all");
    const [dueFilter, setDueFilter] = useState("");
    const [assigneeFilter, setAssigneeFilter] = useState("");
    const [selectedTask, setSelectedTask] = useState(null);
    const [completedOpen, setCompletedOpen] = useState(false);
    const [realtimeConnected, setRealtimeConnected] = useState(false);
    const [missedReminders, setMissedReminders] = useState([]);
    const [snoozingId, setSnoozingId] = useState(null);
    const pollReadyRef = useRef(false);

    const refreshTasks = useCallback(
        async (silent = false) => {
            try {
                const data = await api.fetchTasks();
                setTasks((prev) => {
                    if (silent && pollReadyRef.current && isStaff) {
                        const newOnes = detectNewTasks(prev, data);
                        newOnes.forEach((t) => notifyNewTask(t, { role }));
                    }
                    pollReadyRef.current = true;
                    return data;
                });
                // Keep detail drawer in sync after poll
                setSelectedTask((prev) => {
                    if (!prev) return prev;
                    const updated = data.find((t) => t.id === prev.id);
                    return updated ?? prev;
                });
                syncTaskRemindersFromTasks(data, { role }).catch(() => {});
                const missed = findMissedReminders(data, role);
                setMissedReminders(missed);
                notifyMissedReminders(data, {
                    role,
                    onOpenTask: (task) => setSelectedTask(task),
                });
            } catch (error) {
                if (!silent) toast.error(error.message);
            }
        },
        [api, isStaff, role]
    );

    const fetchTasks = useCallback(() => refreshTasks(false), [refreshTasks]);

    const handleRealtimeEvent = useCallback(
        (payload) => {
            if (payload.type === "connected") {
                setRealtimeConnected(true);
                return;
            }
            if (payload.type === "disconnected") {
                setRealtimeConnected(false);
                return;
            }
            if (payload.type === "task:created" && isStaff && payload.task) {
                notifyNewTask(payload.task, { role });
            }
            refreshTasks(true);
        },
        [isStaff, role, refreshTasks]
    );

    useTaskEvents({
        apiUrl,
        getToken,
        enabled: enablePolling,
        onEvent: handleRealtimeEvent,
    });

    useEffect(() => {
        refreshTasks(false);
    }, [refreshTasks]);

    useEffect(() => {
        if (!enablePolling) return undefined;
        const pollMs = realtimeConnected ? POLL_MS_REALTIME : POLL_MS;
        const tick = () => {
            if (!document.hidden) refreshTasks(true);
        };
        const id = setInterval(tick, pollMs);
        document.addEventListener("visibilitychange", tick);
        return () => {
            clearInterval(id);
            document.removeEventListener("visibilitychange", tick);
        };
    }, [enablePolling, refreshTasks, realtimeConnected]);

    const filteredTasks = useMemo(() => {
        let list = tasks.filter((t) => {
            const statusMatch =
                !statusFilter ||
                (statusFilter === "active"
                    ? t.status !== "Completed"
                    : t.status === statusFilter);
            const priorityMatch =
                !priorityFilter || t.priority?.toLowerCase() === priorityFilter;
            const todayMatch =
                !isStaff || viewFilter !== "today" || isDueTodayOrOverdue(t);
            const dueMatch =
                !dueFilter ||
                (dueFilter === "today" && isDueToday(t)) ||
                (dueFilter === "overdue" && isOverdue(t)) ||
                (dueFilter === "active" && t.status !== "Completed");
            const assigneeMatch =
                !assigneeFilter ||
                String(t.assign_to ?? "unassigned") === String(assigneeFilter);
            return statusMatch && priorityMatch && todayMatch && dueMatch && assigneeMatch;
        });
        if (isStaff) list = sortTasksForStaff(list);
        return list;
    }, [tasks, statusFilter, priorityFilter, viewFilter, dueFilter, assigneeFilter, isStaff]);

    const activeTasks = useMemo(
        () => filteredTasks.filter((t) => t.status !== "Completed"),
        [filteredTasks]
    );

    const completedTasks = useMemo(
        () => filteredTasks.filter((t) => t.status === "Completed"),
        [filteredTasks]
    );

    const todayCount = useMemo(
        () =>
            tasks.filter(
                (t) => t.status !== "Completed" && isDueTodayOrOverdue(t)
            ).length,
        [tasks]
    );

    const updateTaskField = useCallback(
        async (taskId, field, value) => {
            setTasks((prev) =>
                prev.map((t) => (t.id === taskId ? { ...t, [field]: value } : t))
            );
            try {
                const serverUpdates = await api.updateField(taskId, field, value);
                setTasks((prev) =>
                    prev.map((t) => (t.id === taskId ? { ...t, ...serverUpdates } : t))
                );
                setSelectedTask((prev) =>
                    prev && prev.id === taskId ? { ...prev, ...serverUpdates } : prev
                );
            } catch (err) {
                console.error("Update failed", err);
                fetchTasks();
            }
        },
        [api, fetchTasks]
    );

    const handleCompleteTask = useCallback(
        async (id, taskStatus, proofFile = null) => {
            try {
                const task = tasks.find((t) => t.id === id);
                const isCompleting = taskStatus !== "Completed";

                if (proofFile && isCompleting) {
                    await api.updateField(id, "attachment", proofFile);
                }

                await api.logHistory(
                    id,
                    isCompleting ? "Completed" : "In Progress"
                );

                if (isCompleting && task && isRecurringTask(task)) {
                    if (task) cancelTaskReminder(task);
                    const result = await api.completeOccurrence(id);
                    toast.success(
                        result?.next
                            ? "Task completed — next occurrence scheduled"
                            : proofFile
                              ? "Task completed with photo proof"
                              : "Recurring task completed"
                    );
                } else {
                    await api.updateStatus(id, isCompleting ? "Completed" : "In Progress");
                    toast.success(
                        proofFile
                            ? "Task completed with photo proof"
                            : isCompleting
                              ? "Task completed"
                              : "Task marked in progress"
                    );
                }

                fetchTasks();
            } catch (error) {
                toast.error(error.message);
            }
        },
        [api, fetchTasks, tasks]
    );

    const handleSnoozeTask = useCallback(
        async (taskId, minutes = 10) => {
            setSnoozingId(taskId);
            try {
                const task = tasks.find((t) => t.id === taskId);
                await api.snoozeTask(taskId, minutes);
                if (task) cancelTaskReminder(task);
                toast.success(`Reminder snoozed for ${minutes} minutes`);
                await fetchTasks();
            } catch (error) {
                toast.error(error.message);
            } finally {
                setSnoozingId(null);
            }
        },
        [api, fetchTasks, tasks]
    );

    const handleTogglePause = useCallback(
        async (taskId, isPaused, reason = null) => {
            try {
                await api.logHistory(taskId, isPaused ? "Continue Task" : "Paused", reason);
                await api.updatePaused(taskId, isPaused ? false : true);
                toast.success(isPaused ? "Task continued" : "Task paused");
                fetchTasks();
            } catch (error) {
                toast.error(error.message);
            }
        },
        [api, fetchTasks]
    );

    const handleDeleteTask = useCallback(
        async (taskId) => {
            const result = await Swal.fire({
                title: "Are you sure?",
                text: "This action cannot be undone!",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes, delete it!",
                cancelButtonText: "Cancel",
                customClass: {
                    confirmButton: "swal2-delete-confirm",
                },
            });

            if (!result.isConfirmed) return;

            try {
                await api.deleteTask(taskId);
                const removed = tasks.find((t) => t.id === taskId);
                if (removed) cancelTaskReminder(removed);
                Swal.fire("Deleted!", "Task has been deleted.", "success");
                setTasks((prev) => prev.filter((t) => t.id !== taskId));
                setSelectedTask((prev) => (prev?.id === taskId ? null : prev));
            } catch (error) {
                Swal.fire("Error!", error.message || "Failed to delete task", "error");
            }
        },
        [api, tasks]
    );

    const handleDragEnd = useCallback(
        (event) => {
            const { active, over } = event;
            if (!over || active.id === over.id) return;

            setTasks((prev) => {
                const oldIndex = prev.findIndex((t) => t.id === active.id);
                const newIndex = prev.findIndex((t) => t.id === over.id);
                const newTasks = arrayMove(prev, oldIndex, newIndex);
                const orderedTasks = newTasks.map((t, index) => ({
                    id: t.id,
                    position: index,
                }));
                api.reorderTasks(orderedTasks).catch((err) =>
                    console.error("Failed to update order:", err)
                );
                return newTasks;
            });
        },
        [api]
    );

    const bulkAssign = useCallback(
        async (taskIds, assignTo) => {
            try {
                await api.bulkAssign(taskIds, assignTo);
                toast.success(`${taskIds.length} task(s) reassigned`);
                fetchTasks();
            } catch (error) {
                toast.error(error.message);
            }
        },
        [api, fetchTasks]
    );

    return {
        tasks,
        setTasks,
        fetchTasks,
        statusFilter,
        setStatusFilter,
        priorityFilter,
        setPriorityFilter,
        viewFilter,
        setViewFilter,
        dueFilter,
        setDueFilter,
        assigneeFilter,
        setAssigneeFilter,
        todayCount,
        selectedTask,
        setSelectedTask,
        completedOpen,
        setCompletedOpen,
        filteredTasks,
        activeTasks,
        completedTasks,
        updateTaskField,
        handleCompleteTask,
        handleSnoozeTask,
        snoozingId,
        handleTogglePause,
        handleDeleteTask,
        handleDragEnd,
        bulkAssign,
        realtimeConnected,
        handleTaskReminderDeepLink: (params) =>
            handleTaskReminderDeepLink({ ...params, apiUrl, getToken, role }),
        missedReminders,
        dismissMissedReminder: (taskId) => {
            try {
                const day = new Date().toISOString().slice(0, 10);
                window.localStorage?.setItem(`beentara_missed_reminder_${taskId}_${day}`, "1");
            } catch {
                /* ignore */
            }
            setMissedReminders((prev) => prev.filter((t) => t.id !== taskId));
        },
    };
}
